import React, { useEffect, useState } from "react";
import { Table, Button, Tag, Row, Col, Space, Dropdown, Checkbox, Input, Popover, Tooltip, Form, Modal, Select } from "antd";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { fetchOnboardings, updateJoiningStatus } from "../onboardingThunks";
import { useNavigate } from "react-router-dom";
import { useIsAdminUser } from "../../../lib/accessList";

const joiningStatusColor = {
  pending: "orange",
  joining_confirmed: "blue",
  joined: "green",
  backed_out: "red",
  no_response: "volcano",
  offer_rejected: "magenta",
  joining_postponed: "gold",
  cancelled: "default",
};

const OnboardingList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading } = useSelector((s) => s.onboarding);
  const [searchText, setSearchText] = useState("");
  const [visibleColumns, setVisibleColumns] = useState([
    "full_name",
    "email",
    "phone_no",
    "status",
    "joining_status",
  ]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [isAdminUser, isAdminUserSet] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [statusForm] = Form.useForm();

  const authInfo = useSelector((state) => state.auth?.auth_info);
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;


  const filteredData = list.filter((item) => {
    const text = searchText.toLowerCase();

    return (
      item.full_name?.toLowerCase().includes(text) ||
      item.email?.toLowerCase().includes(text) ||
      item.phone_no?.toLowerCase().includes(text)
    );
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleJoiningStatusUpdate = async () => {
    try {
      const values = await statusForm.validateFields();
      const res = await dispatch(
        updateJoiningStatus({
          id: selectedRow.id,
          ...values,
        })
      ).unwrap();

      if (res.error) {
        return Swal.fire({
          icon: "error",
          title: res.msg,
        });
      };

      Swal.fire({
        icon: "success",
        title: res.msg,
      });

      setStatusModalOpen(false);
      setSelectedRow(null);
      statusForm.resetFields();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: err?.msg || err?.message || "Unable to update status",
      });
    }
  };



  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',

      // Admin role
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminUserSet(isAdminHR);
  }, [authInfo]);

  useEffect(() => {
    dispatch(fetchOnboardings());
  }, [dispatch]);

  const allColumns = [
    {
      title: "No",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile",
      key: "phone_no",
      render: (row) =>
        row.country_code
          ? `+${row.country_code} ${row.phone_no}`
          : row.phone_no || "-",
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: "Designation",
      dataIndex: "designation_name",
      key: "designation_name",
    },
    {
      title: "Joining Date",
      dataIndex: "joining_date",
      key: "joining_date",
    },
    {
      title: "Status",
      key: "status",
      render: (row) => {
        switch (row.status) {
          case "converted":
            return <Tag color="green">Converted</Tag>;
          case "in_progress":
            return <Tag color="blue">In Progress</Tag>;
          case "rejected":
            return <Tag color="red">Rejected</Tag>;
          default:
            return <Tag color="orange">New</Tag>;
        }
      },
    },
    {
      title: "Joining Status",
      key: "joining_status",
      render: (row) => (
        <Tooltip
          placement="bottomLeft"
          title={
            row.joining_status_reason || row.remarks ? (
              <>
                <div><strong>Reason:</strong></div>
                <div>{row.joining_status_reason || "-"}</div>

                <br />

                <div><strong>Remarks:</strong></div>
                <div>{row.remarks || "-"}</div>
              </>
            ) : null
          }
          trigger={["hover", "click"]}
        >
          <Tag color={joiningStatusColor[row.joining_status]} >
            {row.joining_status
              ?.replaceAll("_", " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}

            {row.joining_status_reason || row.remarks ? <InfoCircleOutlined
              style={{
                marginLeft: "4px",
                color: "#0e3e8b",
                cursor: "pointer",
              }}
            /> : null}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, row) => {
        const canEditRecord =
          row.status !== "converted" &&
          (isAdminUser || canEdit);

        const canConvert =
          row.status !== "converted" &&
          row.joining_status === "joined" &&
          (isAdminUser || canEdit);
        return (
          <Space>
            <Tooltip
              title={
                row.status === "converted"
                  ? "Converted onboarding records cannot be edited."
                  : !(isAdminUser || canEdit)
                    ? "You don't have permission."
                    : "Edit onboarding"
              }
            >
              <Button
                type="primary"
                disabled={!canEditRecord}
                onClick={() =>
                  navigate("/hrpayroll/onboarding/add", {
                    state: { id: row.id },
                  })
                }
              >
                Edit
              </Button>
            </Tooltip>

            <Tooltip
              title={
                row.status === "converted"
                  ? "Already converted to employee."
                  : !(isAdminUser || canEdit)
                    ? "You don't have permission."
                    : row.joining_status !== "joined"
                      ? "Candidate must be marked as Joined before conversion."
                      : "Convert this onboarding record to an employee."
              }
            >
              <Button
                disabled={!canConvert}
                onClick={() =>
                  navigate(`/hrpayroll/employees?onboardId=${row.id}`)
                }
              >
                Convert
              </Button>
            </Tooltip>
            <Button
              onClick={() => {
                setSelectedRow(row);

                statusForm.setFieldsValue({
                  joining_status: row.joining_status,
                  joining_status_reason: row.joining_status_reason,
                  remarks: row.remarks,
                });

                setStatusModalOpen(true);
              }}
              disabled={row.status === "converted"}
            >
              Joining Status
            </Button>
          </Space>
        )
      },
    },
  ];

  const columns = allColumns.filter(
    (col) =>
      col.key === "index" ||
      col.key === "action" ||
      visibleColumns.includes(col.key)
  );

  const columnOptions = allColumns
    .filter((c) => !["index", "action"].includes(c.key))
    .map((c) => ({
      label: c.title,
      value: c.key,
    }));

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        gutter={[12, 12]}
        style={{ marginBottom: 20 }}
      >
        <Col xs={24} sm={12}>
          <Input.Search
            placeholder="Search onboarding..."
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "100%", maxWidth: 250 }}
          />
        </Col>

        <Col xs={24} sm="auto">
          <Space wrap>
            <Popover
              trigger="click"
              placement="bottomRight"
              content={
                <div style={{ padding: 10 }}>
                  <Checkbox.Group
                    options={columnOptions}
                    value={visibleColumns}
                    onChange={(vals) => setVisibleColumns(vals)}
                  />
                </div>
              }
            >
              <Button>Columns</Button>
            </Popover>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/hrpayroll/onboarding/add")}
            >
              Add Onboarding
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        loading={loading}
        rowKey="id"
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Update Joining Status"
        open={statusModalOpen}
        onCancel={() => {
          setStatusModalOpen(false);
          setSelectedRow(null);
          statusForm.resetFields();
        }}
        onOk={handleJoiningStatusUpdate}
        okText="Update"
      >
        <Form layout="vertical" form={statusForm}>
          <Form.Item
            name="joining_status"
            label="Joining Status"
            rules={[
              {
                required: true,
                message: "Select joining status",
              },
            ]}
          >
            <Select
              options={[
                { label: "Pending", value: "pending" },
                {
                  label: "Joining Confirmed",
                  value: "joining_confirmed",
                },
                {
                  label: "Joined",
                  value: "joined",
                },
                {
                  label: "Backed Out",
                  value: "backed_out",
                },
                {
                  label: "No Response",
                  value: "no_response",
                },
                {
                  label: "Offer Rejected",
                  value: "offer_rejected",
                },
                {
                  label: "Joining Postponed",
                  value: "joining_postponed",
                },
                {
                  label: "Cancelled",
                  value: "cancelled",
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) =>
              prev.joining_status !== cur.joining_status
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const requireReason = [
                "backed_out",
                "no_response",
                "offer_rejected",
                "cancelled",
              ].includes(getFieldValue("joining_status"));

              return (
                <Form.Item
                  label="Reason"
                  name="joining_status_reason"
                  rules={
                    requireReason
                      ? [
                        {
                          required: true,
                          message: "Reason is required",
                        },
                      ]
                      : []
                  }
                >
                  <Input />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OnboardingList;